import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Space,
  Popconfirm,
  Tag,
  Tooltip,
  Upload,
  Row,
  Col,
  Select,
  notification,
  Skeleton
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  SearchOutlined,
  UploadOutlined,
  DownloadOutlined,
  UserOutlined,
  KeyOutlined,
  LoadingOutlined,
  CheckCircleOutlined,
  CloudDownloadOutlined,
  SyncOutlined,
  DatabaseOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import * as XLSX from 'xlsx';
import { Progress } from 'antd';

interface MoodleUser {
  id: number;
  username: string;
  firstname: string;
  lastname: string;
  email: string;
  department: string;
  auth: string;
  confirmed: boolean;
  suspended: boolean;
  lastlogin: number;
  firstaccess: number;
}

interface MoodleUserFormData {
  username: string;
  password: string;
  firstname: string;
  lastname: string;
  email: string;
  department: string;
}

const { confirm } = Modal;

const MoodleUserManagementPage: React.FC = () => {
  const [users, setUsers] = useState<MoodleUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<MoodleUser | null>(null);
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<MoodleUser | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();
  // Loading progress (streaming)
  const [progressVisible, setProgressVisible] = useState(false);
  const [progressPercent, setProgressPercent] = useState(0);
  const [loadedBytes, setLoadedBytes] = useState(0);
  const [totalBytes, setTotalBytes] = useState(0);
  const [totalRecords, setTotalRecords] = useState<number | null>(null);
  // Loading steps for smooth UX
  const [loadingStep, setLoadingStep] = useState<'connecting' | 'downloading' | 'processing' | 'done'>('connecting');
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  // Server-side pagination state
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  // Memoized data source - MUST be at top level, not inside conditional JSX
  const tableDataSource = useMemo(() => users.map((u, idx) => ({ ...u, _key: `${u.id}-${idx}` })), [users]);

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, []);

  const formatBytes = (bytes: number) => {
    if (!bytes) return '0 B';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };

  // Load users khi component mount và khi page/pageSize thay đổi
  useEffect(() => {
    fetchUsers(searchValue, page, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  // Smooth animated progress function for better UX
  const startSmoothProgress = () => {
    // Clear any existing interval
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }
    
    let currentProgress = 0;
    const targetProgress = 85; // Max progress before actual completion
    const duration = 15000; // 15 seconds max
    const intervalTime = 100; // Update every 100ms
    const steps = duration / intervalTime;
    
    // Easing function for smooth deceleration
    const easeOutQuart = (t: number) => 1 - Math.pow(1 - t, 4);
    
    let elapsed = 0;
    progressIntervalRef.current = setInterval(() => {
      elapsed += intervalTime;
      const t = Math.min(elapsed / duration, 1);
      currentProgress = easeOutQuart(t) * targetProgress;
      
      // Update loading step based on progress
      if (currentProgress < 15) {
        setLoadingStep('connecting');
      } else if (currentProgress < 70) {
        setLoadingStep('downloading');
      } else {
        setLoadingStep('processing');
      }
      
      setProgressPercent(Math.round(currentProgress));
      
      if (t >= 1) {
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
        }
      }
    }, intervalTime);
  };

  const stopSmoothProgress = (success: boolean) => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    
    if (success) {
      setLoadingStep('done');
      setProgressPercent(100);
      setTimeout(() => {
        setProgressVisible(false);
        setProgressPercent(0);
        setLoadedBytes(0);
        setTotalBytes(0);
        setLoadingStep('connecting');
      }, 600);
    } else {
      setProgressVisible(false);
      setProgressPercent(0);
      setLoadingStep('connecting');
    }
  };

  const fetchUsers = async (searchTerm?: string, pageArg?: number, pageSizeArg?: number) => {
    try {
      setLoading(true);
      setProgressVisible(true);
      setProgressPercent(2);
      setLoadedBytes(0);
      setTotalBytes(0);
      setTotalRecords(null);
      setLoadingStep('connecting');
      
      // Start smooth animated progress
      startSmoothProgress();
      
      const params = new URLSearchParams();
      params.set('page', String(pageArg ?? page));
      params.set('limit', String(pageSizeArg ?? pageSize));
      if (searchTerm && searchTerm.trim()) params.set('q', searchTerm.trim());
      const url = `/api/v1/moodle/users?${params.toString()}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('quanly_truonghoc_token')}`
        }
      });
      
      if (response.ok) {
        // Try streaming to compute percentage when Content-Length is available
        const contentLengthHeader = response.headers.get('Content-Length') || response.headers.get('content-length');
        const totalLength = contentLengthHeader ? parseInt(contentLengthHeader, 10) : 0;
        if (totalLength > 0) setTotalBytes(totalLength);

        if (response.body && totalLength > 0 && !Number.isNaN(totalLength)) {
          // Stop smooth progress, use actual byte-based progress
          if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = null;
          }
          
          const reader = response.body.getReader();
          const chunks: Uint8Array[] = [];
          let received = 0;
          
          setLoadingStep('downloading');
          
          // Read stream with smooth updates
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            if (value) {
              chunks.push(value);
              received += value.length;
              const percent = Math.max(5, Math.min(95, Math.round((received / totalLength) * 100)));
              setProgressPercent(percent);
              setLoadedBytes(received);
            }
          }
          
          setLoadingStep('processing');
          setProgressPercent(96);
          
          // Combine chunks
          const merged = new Uint8Array(received);
          let position = 0;
          for (const chunk of chunks) {
            merged.set(chunk, position);
            position += chunk.length;
          }
          const text = new TextDecoder('utf-8').decode(merged);
          const result = JSON.parse(text);
          const data = result.data || [];
          if (typeof result.total === 'number') {
            setTotalRecords(result.total);
          }
          setUsers(Array.isArray(data) ? data : []);
          stopSmoothProgress(true);
        } else {
          // Fallback no content-length: use smooth animated progress
          setLoadingStep('downloading');
          const result = await response.json();
          setLoadingStep('processing');
          const data = result.data || [];
          if (typeof result.total === 'number') {
            setTotalRecords(result.total);
          }
          setUsers(Array.isArray(data) ? data : []);
          stopSmoothProgress(true);
        }
      } else {
        await response.json();
        throw new Error('Failed to fetch users');
      }
    } catch (error) {
      message.error('Không thể tải danh sách người dùng');
      stopSmoothProgress(false);
    } finally {
      setLoading(false);
    }
  };

  const showAddModal = () => {
    setEditingUser(null);
    form.resetFields();
    setModalVisible(true);
  };

  const showEditModal = (user: MoodleUser) => {
    setEditingUser(user);
    form.setFieldsValue({
      username: user.username,
      firstname: user.firstname,
      lastname: user.lastname,
      email: user.email,
      department: user.department
    });
    setModalVisible(true);
  };

  const showPasswordModal = (user: MoodleUser) => {
    setSelectedUser(user);
    passwordForm.resetFields();
    setPasswordModalVisible(true);
  };

  const handleCancel = () => {
    setModalVisible(false);
    form.resetFields();
    setEditingUser(null);
  };

  const handlePasswordCancel = () => {
    setPasswordModalVisible(false);
    passwordForm.resetFields();
    setSelectedUser(null);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const url = editingUser ? `/api/v1/moodle/users` : '/api/v1/moodle/users';
      const method = editingUser ? 'PUT' : 'POST';
      const payload = editingUser ? { ...editingUser, ...values } : values;

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('quanly_truonghoc_token')}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        message.success(editingUser ? 'Cập nhật người dùng thành công' : 'Tạo người dùng thành công');
        setModalVisible(false);
        setEditingUser(null);
        form.resetFields();
        fetchUsers(searchValue);
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Có lỗi xảy ra');
      }
    } catch (error) {
      message.error('Không thể lưu người dùng');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async () => {
    try {
      const values = await passwordForm.validateFields();
      setLoading(true);

      const response = await fetch(`/api/v1/moodle/users/${selectedUser?.id}/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('quanly_truonghoc_token')}`
        },
        body: JSON.stringify({ password: values.password })
      });

      if (response.ok) {
        message.success('Đổi mật khẩu thành công');
        setPasswordModalVisible(false);
        setSelectedUser(null);
        passwordForm.resetFields();
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Có lỗi xảy ra');
      }
    } catch (error) {
      message.error('Không thể đổi mật khẩu');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (userId: number) => {
    try {
      setLoading(true);
      const response = await fetch(`/api/v1/moodle/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('quanly_truonghoc_token')}`
        }
      });

      if (response.ok) {
        message.success('Xóa người dùng thành công');
        fetchUsers(searchValue);
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Có lỗi xảy ra');
      }
    } catch (error) {
      message.error('Không thể xóa người dùng');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (!searchValue.trim()) {
      message.warning('Vui lòng nhập từ khóa tìm kiếm');
      return;
    }
    // Reset to first page when searching
    setPage(1);
    fetchUsers(searchValue, 1, pageSize);
  };

  const handleExportExcel = () => {
    const exportData = users.map(user => ({
      'ID': user.id,
      'Tên đăng nhập': user.username,
      'Họ': user.lastname,
      'Tên': user.firstname,
      'Email': user.email,
      'Phòng ban': user.department,
      'Trạng thái': user.suspended ? 'Bị khóa' : 'Hoạt động',
      'Xác nhận': user.confirmed ? 'Đã xác nhận' : 'Chưa xác nhận',
      'Đăng nhập lần cuối': user.lastlogin ? new Date(user.lastlogin * 1000).toLocaleString() : 'Chưa từng đăng nhập'
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Người dùng Moodle');
    XLSX.writeFile(wb, `moodle_users_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const columns: ColumnsType<MoodleUser> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: 'Tên đăng nhập',
      dataIndex: 'username',
      key: 'username',
      width: 140,
      sorter: (a, b) => a.username.localeCompare(b.username),
    },
    {
      title: 'Họ tên',
      key: 'fullname',
      width: 200,
      render: (_, record) => `${record.lastname} ${record.firstname}`,
      sorter: (a, b) => `${a.lastname} ${a.firstname}`.localeCompare(`${b.lastname} ${b.firstname}`),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 200,
      sorter: (a, b) => a.email.localeCompare(b.email),
    },
    {
      title: 'Phòng ban',
      dataIndex: 'department',
      key: 'department',
      width: 150,
      sorter: (a, b) => a.department.localeCompare(b.department),
    },
    {
      title: 'Trạng thái',
      key: 'status',
      width: 120,
      render: (_, record) => (
        <Space>
          <Tag color={record.suspended ? 'red' : 'green'}>
            {record.suspended ? 'Bị khóa' : 'Hoạt động'}
          </Tag>
          {!record.confirmed && (
            <Tag color="orange">Chưa xác nhận</Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Đăng nhập lần cuối',
      key: 'lastlogin',
      width: 180,
      render: (_, record) => {
        if (!record.lastlogin || record.lastlogin === 0) {
          return <span style={{ color: '#999' }}>Chưa từng đăng nhập</span>;
        }
        const date = new Date(record.lastlogin * 1000);
        return (
          <span title={date.toLocaleString('vi-VN')}>
            {date.toLocaleDateString('vi-VN')} {date.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}
          </span>
        );
      },
      sorter: (a, b) => (a.lastlogin || 0) - (b.lastlogin || 0),
    },
    {
      title: 'Thao tác',
      key: 'action',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Sửa thông tin">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => showEditModal(record)}
              size="small"
            />
          </Tooltip>
          <Tooltip title="Đổi mật khẩu">
            <Button
              type="text"
              icon={<KeyOutlined />}
              onClick={() => showPasswordModal(record)}
              size="small"
            />
          </Tooltip>
          <Popconfirm
            title="Bạn có chắc chắn muốn xóa người dùng này?"
            onConfirm={() => handleDelete(record.id)}
            okText="Xóa"
            cancelText="Hủy"
          >
            <Tooltip title="Xóa người dùng">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <UserOutlined />
          <span>Quản lý người dùng Moodle</span>
        </div>
      }
      extra={
        <Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={showAddModal}
          >
            Thêm người dùng
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExportExcel}
            disabled={users.length === 0}
          >
            Xuất Excel
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => fetchUsers()}
          />
        </Space>
      }
    >
      <div style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={8}>
            <Input.Search
              placeholder="Tìm kiếm theo tên đăng nhập, họ tên, email..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onSearch={handleSearch}
              enterButton={<SearchOutlined />}
            />
          </Col>
        </Row>
        {/* Enhanced Loading Progress with Steps */}
        {progressVisible && (
          <div 
            style={{ 
              marginTop: 16, 
              padding: '16px 20px',
              background: 'linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%)',
              borderRadius: 12,
              border: '1px solid #e0e6ed',
              maxWidth: 520
            }}
          >
            {/* Loading Steps Indicator */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 24,
              marginBottom: 12,
              fontSize: 13
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 6,
                color: loadingStep === 'connecting' ? '#1890ff' : 
                       (progressPercent >= 15 ? '#52c41a' : '#999'),
                fontWeight: loadingStep === 'connecting' ? 600 : 400,
                transition: 'all 0.3s ease'
              }}>
                {loadingStep === 'connecting' ? (
                  <SyncOutlined spin style={{ fontSize: 14 }} />
                ) : progressPercent >= 15 ? (
                  <CheckCircleOutlined style={{ fontSize: 14 }} />
                ) : (
                  <CloudDownloadOutlined style={{ fontSize: 14 }} />
                )}
                <span>Kết nối</span>
              </div>
              
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 6,
                color: loadingStep === 'downloading' ? '#1890ff' : 
                       (progressPercent >= 70 ? '#52c41a' : '#999'),
                fontWeight: loadingStep === 'downloading' ? 600 : 400,
                transition: 'all 0.3s ease'
              }}>
                {loadingStep === 'downloading' ? (
                  <LoadingOutlined spin style={{ fontSize: 14 }} />
                ) : progressPercent >= 70 ? (
                  <CheckCircleOutlined style={{ fontSize: 14 }} />
                ) : (
                  <CloudDownloadOutlined style={{ fontSize: 14 }} />
                )}
                <span>Tải dữ liệu</span>
              </div>
              
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 6,
                color: loadingStep === 'processing' ? '#1890ff' : 
                       (loadingStep === 'done' ? '#52c41a' : '#999'),
                fontWeight: loadingStep === 'processing' ? 600 : 400,
                transition: 'all 0.3s ease'
              }}>
                {loadingStep === 'processing' ? (
                  <DatabaseOutlined style={{ fontSize: 14, animation: 'pulse 1s infinite' }} />
                ) : loadingStep === 'done' ? (
                  <CheckCircleOutlined style={{ fontSize: 14 }} />
                ) : (
                  <DatabaseOutlined style={{ fontSize: 14 }} />
                )}
                <span>Xử lý</span>
              </div>
              
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 6,
                color: loadingStep === 'done' ? '#52c41a' : '#999',
                fontWeight: loadingStep === 'done' ? 600 : 400,
                transition: 'all 0.3s ease'
              }}>
                {loadingStep === 'done' ? (
                  <CheckCircleOutlined style={{ fontSize: 14 }} />
                ) : (
                  <CheckCircleOutlined style={{ fontSize: 14 }} />
                )}
                <span>Hoàn tất</span>
              </div>
            </div>
            
            {/* Smooth Animated Progress Bar */}
            <Progress 
              percent={progressPercent} 
              status={loadingStep === 'done' ? 'success' : 'active'}
              strokeColor={{
                '0%': '#108ee9',
                '100%': '#87d068',
              }}
              trailColor="#e0e6ed"
              style={{ 
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            />
            
            {/* Progress Details */}
            <div style={{ 
              marginTop: 8, 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center',
              color: '#666',
              fontSize: 12
            }}>
              <div>
                {totalBytes > 0 ? (
                  <span>📦 {formatBytes(loadedBytes)} / {formatBytes(totalBytes)}</span>
                ) : (
                  <span style={{ 
                    display: 'inline-flex', 
                    alignItems: 'center', 
                    gap: 4 
                  }}>
                    <LoadingOutlined spin style={{ fontSize: 11 }} />
                    {loadingStep === 'connecting' && 'Đang kết nối tới Moodle...'}
                    {loadingStep === 'downloading' && 'Đang tải dữ liệu từ server...'}
                    {loadingStep === 'processing' && 'Đang xử lý dữ liệu...'}
                    {loadingStep === 'done' && 'Hoàn tất!'}
                  </span>
                )}
              </div>
              <div>
                {typeof totalRecords === 'number' && (
                  <span>📊 {totalRecords.toLocaleString()} người dùng</span>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* CSS Animation for pulse effect */}
        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}</style>
      </div>

      {/** Table with Skeleton loading for smooth UX */}
      {loading && users.length === 0 ? (
        // Show skeleton when initially loading (no data yet)
        <div style={{ padding: '16px 0' }}>
          <Skeleton active paragraph={{ rows: 8 }} />
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={tableDataSource}
          rowKey="_key"
          loading={{
            spinning: loading && users.length > 0,
            indicator: <LoadingOutlined style={{ fontSize: 24 }} spin />,
            tip: 'Đang cập nhật...'
          }}
          pagination={{
            total: typeof totalRecords === 'number' ? totalRecords : users.length,
            current: page,
            pageSize: pageSize,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) => `${range[0]}-${range[1]} của ${total.toLocaleString()} người dùng`,
            onChange: (p, ps) => {
              const sizeChanged = ps !== pageSize;
              setPage(p);
              if (sizeChanged) setPageSize(ps);
              fetchUsers(searchValue, p, ps);
            }
          }}
          scroll={{ x: 1200, y: 'calc(100vh - 420px)' }}
          size="small"
          style={{
            transition: 'opacity 0.3s ease',
            opacity: loading ? 0.7 : 1
          }}
        />
      )}

      {/* Modal thêm/sửa người dùng */}
      <Modal
        title={editingUser ? 'Sửa thông tin người dùng' : 'Thêm người dùng mới'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={handleCancel}
        confirmLoading={loading}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            department: 'Sinh viên'
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="username"
                label="Tên đăng nhập"
                rules={[{ required: true, message: 'Vui lòng nhập tên đăng nhập' }]}
              >
                <Input disabled={!!editingUser} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: 'Vui lòng nhập email' },
                  { type: 'email', message: 'Email không hợp lệ' }
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="lastname"
                label="Họ"
                rules={[{ required: true, message: 'Vui lòng nhập họ' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="firstname"
                label="Tên"
                rules={[{ required: true, message: 'Vui lòng nhập tên' }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="department"
                label="Phòng ban"
              >
                <Input placeholder="Nhập phòng ban" />
              </Form.Item>
            </Col>
            {!editingUser && (
              <Col span={12}>
                <Form.Item
                  name="password"
                  label="Mật khẩu"
                  rules={[{ required: true, message: 'Vui lòng nhập mật khẩu' }]}
                >
                  <Input.Password />
                </Form.Item>
              </Col>
            )}
          </Row>
        </Form>
      </Modal>

      {/* Modal đổi mật khẩu */}
      <Modal
        title="Đổi mật khẩu"
        open={passwordModalVisible}
        onOk={handlePasswordSubmit}
        onCancel={handlePasswordCancel}
        confirmLoading={loading}
        width={400}
      >
        <Form
          form={passwordForm}
          layout="vertical"
        >
          <Form.Item
            name="password"
            label="Mật khẩu mới"
            rules={[
              { required: true, message: 'Vui lòng nhập mật khẩu mới' },
              { min: 6, message: 'Mật khẩu phải có ít nhất 6 ký tự' }
            ]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="Xác nhận mật khẩu"
            dependencies={['password']}
            rules={[
              { required: true, message: 'Vui lòng xác nhận mật khẩu' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Mật khẩu xác nhận không khớp'));
                },
              }),
            ]}
          >
            <Input.Password />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default MoodleUserManagementPage;
